import { QueryClient } from '@tanstack/react-query';

import { MessageStatus } from './useSearchTickets.interface';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import zendeskGateway from 'services/help-center/zendeskBackendGateway';
import {
    ApiResponse,
    Ticket,
} from 'modules/IMC/MessageCenter/components/MessageCenterContent/MessageList/MessageList.interface';
import { OPEN_MESSAGES_STATUSES } from 'services/help-center/constants';
import { mergeRecentlyCreatedTickets, sortByLastPublicComment } from 'services/help-center/recentlyCreatedTickets';

/** The shared all-status requested-tickets list that both tabs are derived from. */
export const requestedTicketsQueryKey = (zdUserId: number) => ['messageCenterTickets', zdUserId, '', 'all'] as const;

export const filterTickets = (tickets: Ticket[], isOpenMessagesStatus: boolean | undefined) => {
    return tickets.filter((ticket) => {
        const isOpenStatus = OPEN_MESSAGES_STATUSES.includes(ticket.status as MessageStatus);
        return ticket.is_public && (isOpenMessagesStatus ? isOpenStatus : !isOpenStatus);
    });
};

/**
 * The unsearched requested-tickets list, every status in one cache entry. `/tickets/requested` takes
 * no status filter, so the Open and Closed tabs are both derived from this one response.
 */
async function fetchAllRequestedTickets(zdUserId: number): Promise<ApiResponse> {
    try {
        const response = await zendeskGateway.get(`/users/${zdUserId}/tickets/requested`, {
            params: {
                include: 'last_comment',
                sort_by: 'created_at',
                sort_order: 'desc',
                size: 20,
            },
        });

        const tickets = ((response.data.tickets || []) as Ticket[])
            .filter((ticket) => ticket.is_public)
            .sort(sortByLastPublicComment);

        return { results: tickets, count: tickets.length, facets: {}, next_page: null, previous_page: null };
    } catch (error) {
        // Reported as the per-tab fetch it replaces, so the same logs and monitors see it.
        sendDatadogRumError('fetchTickets', error, {
            source: 'fetchTickets',
            zdUserId,
            searchQuery: '',
            status: 'all',
        });
        throw new Error('Failed to fetch tickets. Please try again later.');
    }
}

export function requestedTicketsQueryFn(queryClient: QueryClient, zdUserId: number) {
    const queryKey = requestedTicketsQueryKey(zdUserId);
    return async () => mergeRecentlyCreatedTickets(queryClient, queryKey, await fetchAllRequestedTickets(zdUserId));
}

/** One tab's view of the shared list: the same status split and order the per-tab fetch produced. */
export function selectTicketsByStatus(data: ApiResponse, status?: MessageStatus[]): ApiResponse {
    const isOpenMessagesStatus = status?.every((s) => OPEN_MESSAGES_STATUSES.includes(s));
    const results = filterTickets(data.results, isOpenMessagesStatus).sort(sortByLastPublicComment);
    return { ...data, results, count: results.length };
}
